import React, { useEffect, useState, useRef } from "react";
import { useHistory, useLocation } from "react-router-dom";
import "react-toastify/dist/ReactToastify.css";
import "tippy.js/dist/tippy.css";
import "tippy.js/themes/material.css";
import {
  Card,
  CardContent,
  Typography,
  Checkbox,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab,
  Tooltip,
} from "@mhome/ui";
import CircularProgress from "@mui/material/CircularProgress";
import { useRecoilState, useRecoilValue } from "recoil";
import { automationActiveTabState, wsReadyState } from "../global-state";
import { useComponentBackgroundColor } from "../common/adaptive-theme-provider";
import ErrorIcon from "@mui/icons-material/Error";
import DeleteIcon from "@mui/icons-material/Delete";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CloseIcon from "@mui/icons-material/Close";
import PlayCircleFilledWhiteIcon from "@mui/icons-material/PlayCircleFilledWhite";
import TokenIcon from "@mui/icons-material/Token";
import {
  disableAutomation,
  enableAutomation,
  listAutomations,
  batchDelete,
  deleteAutomation,
  runningAutomation,
} from "../service/automation-service";
import { useToast } from "../common/toast";
import { useEventEmitter } from "../ws/event-context";
import { useWebSocket } from "../ws/websocket";
import dayjs from "dayjs";
import { useCustomFormat } from "../views/after-init";
import AutomationFilter from "./automation-filter";
import MSwitch from "../common/switch";
import ResponsiveCardGrid from "../common/responsive-card-grid";
import { useMediaQuery } from "@mui/material";
import { getTagIcon } from "../common/tag";
import MenuCard from "../component/menu-card";
import { createGlobalStyle } from "styled-components";

const GlobalStyle = createGlobalStyle`
  @keyframes shake {
    0%, 100% { transform: translateX(0); }
    10%, 30%, 50%, 70%, 90% { transform: translateX(-1px); }
    20%, 40%, 60%, 80% { transform: translateX(1px); }
  }
`;

const AutomationListView = ({
  showFilter = false,
  setShowFilter,
  isChatSidebarVisible = false,
}) => {
  const history = useHistory();
  const { sendAndWaitWs } = useWebSocket();
  const wsReady = useRecoilValue(wsReadyState);
  const { successToast, errorToast } = useToast();
  const customFormat = useCustomFormat();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useRecoilState(automationActiveTabState);
  const [automations, setAutomations] = useState([]);
  const [taskFlowAutomations, setTaskFlowAutomations] = useState([]);
  const [taskHistories, setTaskHistories] = useState([]);
  const [currentTime, setCurrentTime] = useState(dayjs());
  const [sortBy, setSortBy] = useState("created");
  const [filterText, setFilterText] = useState("");
  const [isShowFilter, setIsShowFilter] = useState(showFilter);
  const [selectedTags, setSelectedTags] = useState(["all"]);
  const [tags, setTags] = useState(["all"]);
  const [selectedEnabled, setSelectedEnabled] = useState(["all"]);
  const [selectedError, setSelectedError] = useState(["all"]);
  const [selectedRunning, setSelectedRunning] = useState(["all"]);
  const [isMultiSelectMode, setIsMultiSelectMode] = useState(false);
  const [selectedRecurrings, setSelectedRecurrings] = useState([]);
  const [selectedTaskFlows, setSelectedTaskFlows] = useState([]);
  const [selectedTaskHistories, setSelectedTaskHistories] = useState([]);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [isRunning, setIsRunning] = useState("");
  const [singleDeleteConfirmOpen, setSingleDeleteConfirmOpen] = useState(false);
  const [deleteTargetId, setDeleteTargetId] = useState(null);
  const { subscribe } = useEventEmitter();
  const lastStatusTimestampRef = useRef(0);
  const isMdOrLgScreen = useMediaQuery("@media (min-width:960px)");
  const [enablingDisablingId, setEnablingDisablingId] = useState(null);
  const location = useLocation();
  const [shakingCardId, setShakingCardId] = useState(null);

  useEffect(() => {
    setIsShowFilter(showFilter);
  }, [showFilter]);

  useEffect(() => {
    const intervalId = setInterval(() => {
      setCurrentTime(dayjs());
    }, 60000);

    return () => clearInterval(intervalId);
  }, []);

  useEffect(() => {
    const { unsubscribe } = subscribe((target, data) => {
      if (target === "/automation/history/reportUpdate") {
        const payload = JSON.parse(data.payload);
        if (payload) {
          updateAutomation(payload);
        }
      } else if (target === "/automation/runningStatus/reportUpdate") {
        const payload = JSON.parse(data.payload);

        if (payload && payload.timestamp > lastStatusTimestampRef.current) {
          lastStatusTimestampRef.current = payload.timestamp;
          updateAutomation(payload);
        }
      }
    });
    return () => {
      unsubscribe();
    };
  }, []);

  useEffect(() => {
    if (wsReady) {
      handleListAll();
    }
  }, [wsReady]);

  useEffect(() => {
    const savedFilterParams = localStorage.getItem("filterParams-recurring");
    if (savedFilterParams) {
      setShowFilter(true);
      const parsedParams = JSON.parse(savedFilterParams);
      if (parsedParams.filter) {
        setFilterText(parsedParams.filter);
      }
      if (parsedParams.sort) {
        setSortBy(parsedParams.sort);
      }

      if (parsedParams.tags) {
        const tagsArray = parsedParams.tags.split(",");
        setSelectedTags(
          tagsArray.length > 0 && tagsArray[0] !== "" ? tagsArray : ["all"]
        );
      }

      if (parsedParams.able) {
        const ableArray = parsedParams.able.split(",");
        setSelectedEnabled(
          ableArray.length > 0 && ableArray[0] !== "" ? ableArray : ["all"]
        );
      }

      if (parsedParams.error) {
        const errorArray = parsedParams.error.split(",");
        setSelectedError(
          errorArray.length > 0 && errorArray[0] !== "" ? errorArray : ["all"]
        );
      }

      if (parsedParams.running) {
        const runningArray = parsedParams.running.split(",");
        setSelectedRunning(
          runningArray.length > 0 && runningArray[0] !== ""
            ? runningArray
            : ["all"]
        );
      }

      updateURL(parsedParams);
    } else {
      setFilterText("");
      setSortBy("created");
      setSelectedTags(["all"]);
      setSelectedEnabled(["all"]);
      setSelectedError(["all"]);
      setSelectedRunning(["all"]);
      updateURL("");
    }
  }, [showFilter]);

  const updateURL = (updates) => {
    if (!updates) {
      history.replace("/automation/list");
      return;
    }
    const currentParams = new URLSearchParams(location.search);

    Object.entries(updates).forEach(([key, value]) => {
      if (
        value &&
        value !== "" &&
        !(Array.isArray(value) && value.length === 0)
      ) {
        currentParams.set(key, value);
      } else {
        currentParams.delete(key);
      }
    });

    const newURL = `${location.pathname}?${currentParams.toString()}`;
    history.replace(newURL);
  };

  const sortedAutomations =
    automations
      .filter((a) => {
        if (!filterText) return true;
        const searchTerms = filterText
          .toLowerCase()
          .split(" ")
          .filter((term) => term.length > 0);
        const searchableText = a.name.toLowerCase();
        return searchTerms.every((term) => searchableText.includes(term));
      })
      .sort((a, b) => {
        if (sortBy === "executed") {
          const aTimestamp = a.timestamp
            ? dayjs(a.timestamp).valueOf()
            : Number.NEGATIVE_INFINITY;
          const bTimestamp = b.timestamp
            ? dayjs(b.timestamp).valueOf()
            : Number.NEGATIVE_INFINITY;
          return bTimestamp - aTimestamp;
        } else if (sortBy === "name") {
          const aName = a.name.toLowerCase();
          const bName = b.name.toLowerCase();

          const aIsLetter = /^[a-zA-Z]/.test(aName);
          const bIsLetter = /^[a-zA-Z]/.test(bName);
          if (aIsLetter && !bIsLetter) return -1;
          if (!aIsLetter && bIsLetter) return 1;
          return aName.localeCompare(bName);
        } else if (sortBy === "created") {
          const aCreated = a.createdAt
            ? dayjs(a.createdAt).valueOf()
            : Number.NEGATIVE_INFINITY;
          const bCreated = b.createdAt
            ? dayjs(b.createdAt).valueOf()
            : Number.NEGATIVE_INFINITY;
          return bCreated - aCreated;
        } else if (sortBy === "opp-created") {
          const aCreated = a.createdAt
            ? dayjs(a.createdAt).valueOf()
            : Number.POSITIVE_INFINITY;
          const bCreated = b.createdAt
            ? dayjs(b.createdAt).valueOf()
            : Number.POSITIVE_INFINITY;
          return aCreated - bCreated;
        } else if (sortBy === "opp-executed") {
          const aTimestamp = a.timestamp
            ? dayjs(a.timestamp).valueOf()
            : Number.POSITIVE_INFINITY;
          const bTimestamp = b.timestamp
            ? dayjs(b.timestamp).valueOf()
            : Number.POSITIVE_INFINITY;
          return aTimestamp - bTimestamp;
        } else if (sortBy === "opp-name") {
          const aName = a.name.toLowerCase();
          const bName = b.name.toLowerCase();

          const aIsLetter = /^[a-zA-Z]/.test(aName);
          const bIsLetter = /^[a-zA-Z]/.test(bName);
          if (aIsLetter && !bIsLetter) return 1;
          if (!aIsLetter && bIsLetter) return -1;
          return bName.localeCompare(aName);
        }
      }) || [];

  const sortedTaskFlowAutomations =
    taskFlowAutomations
      .filter((a) => {
        if (!filterText) return true;
        const searchTerms = filterText
          .toLowerCase()
          .split(" ")
          .filter((term) => term.length > 0);
        const searchableText = a.name.toLowerCase();
        return searchTerms.every((term) => searchableText.includes(term));
      })
      .sort((a, b) => {
        if (sortBy === "executed") {
          const aTimestamp = a.timestamp
            ? dayjs(a.timestamp).valueOf()
            : Number.NEGATIVE_INFINITY;
          const bTimestamp = b.timestamp
            ? dayjs(b.timestamp).valueOf()
            : Number.NEGATIVE_INFINITY;
          return bTimestamp - aTimestamp;
        } else if (sortBy === "name") {
          const aName = a.name.toLowerCase();
          const bName = b.name.toLowerCase();

          const aIsLetter = /^[a-zA-Z]/.test(aName);
          const bIsLetter = /^[a-zA-Z]/.test(bName);
          if (aIsLetter && !bIsLetter) return -1;
          if (!aIsLetter && bIsLetter) return 1;
          return aName.localeCompare(bName);
        } else if (sortBy === "created") {
          const aCreated = a.createdAt
            ? dayjs(a.createdAt).valueOf()
            : Number.NEGATIVE_INFINITY;
          const bCreated = b.createdAt
            ? dayjs(b.createdAt).valueOf()
            : Number.NEGATIVE_INFINITY;
          return bCreated - aCreated;
        } else if (sortBy === "opp-created") {
          const aCreated = a.createdAt
            ? dayjs(a.createdAt).valueOf()
            : Number.POSITIVE_INFINITY;
          const bCreated = b.createdAt
            ? dayjs(b.createdAt).valueOf()
            : Number.POSITIVE_INFINITY;
          return aCreated - bCreated;
        } else if (sortBy === "opp-executed") {
          const aTimestamp = a.timestamp
            ? dayjs(a.timestamp).valueOf()
            : Number.POSITIVE_INFINITY;
          const bTimestamp = b.timestamp
            ? dayjs(b.timestamp).valueOf()
            : Number.POSITIVE_INFINITY;
          return aTimestamp - bTimestamp;
        } else if (sortBy === "opp-name") {
          const aName = a.name.toLowerCase();
          const bName = b.name.toLowerCase();

          const aIsLetter = /^[a-zA-Z]/.test(aName);
          const bIsLetter = /^[a-zA-Z]/.test(bName);
          if (aIsLetter && !bIsLetter) return 1;
          if (!aIsLetter && bIsLetter) return -1;
          return bName.localeCompare(aName);
        }
      }) || [];

  const sortedTaskHistories =
    taskHistories
      .filter((a) => {
        if (!filterText) return true;
        const searchTerms = filterText
          .toLowerCase()
          .split(" ")
          .filter((term) => term.length > 0);
        const searchableText = a.name.toLowerCase();
        return searchTerms.every((term) => searchableText.includes(term));
      })
      .filter((automation) => {
        const tagMatch =
          selectedTags.includes("all") ||
          (automation.tags &&
            automation.tags.filter((x) => selectedTags.includes(x)).length ===
              selectedTags.length);

        const ableMatch = true;

        const errorMatch =
          selectedError.includes("all") ||
          (selectedError.includes("error") && !automation.runnable) ||
          (selectedError.includes("no-error") && automation.runnable);

        const runningMatch =
          selectedRunning.includes("all") ||
          (selectedRunning.includes("running") && automation.running) ||
          (selectedRunning.includes("not-running") && !automation.running);

        return tagMatch && ableMatch && errorMatch && runningMatch;
      })
      .sort((a, b) => {
        if (sortBy === "executed") {
          const aTimestamp = a.timestamp
            ? dayjs(a.timestamp).valueOf()
            : Number.NEGATIVE_INFINITY;
          const bTimestamp = b.timestamp
            ? dayjs(b.timestamp).valueOf()
            : Number.NEGATIVE_INFINITY;
          return bTimestamp - aTimestamp;
        } else if (sortBy === "name") {
          const aName = a.name.toLowerCase();
          const bName = b.name.toLowerCase();

          const aIsLetter = /^[a-zA-Z]/.test(aName);
          const bIsLetter = /^[a-zA-Z]/.test(bName);
          if (aIsLetter && !bIsLetter) return -1;
          if (!aIsLetter && bIsLetter) return 1;
          return aName.localeCompare(bName);
        } else if (sortBy === "created") {
          const aCreated = a.createdAt
            ? dayjs(a.createdAt).valueOf()
            : Number.NEGATIVE_INFINITY;
          const bCreated = b.createdAt
            ? dayjs(b.createdAt).valueOf()
            : Number.NEGATIVE_INFINITY;
          return bCreated - aCreated;
        } else if (sortBy === "opp-created") {
          const aCreated = a.createdAt
            ? dayjs(a.createdAt).valueOf()
            : Number.POSITIVE_INFINITY;
          const bCreated = b.createdAt
            ? dayjs(b.createdAt).valueOf()
            : Number.POSITIVE_INFINITY;
          return aCreated - bCreated;
        } else if (sortBy === "opp-executed") {
          const aTimestamp = a.timestamp
            ? dayjs(a.timestamp).valueOf()
            : Number.POSITIVE_INFINITY;
          const bTimestamp = b.timestamp
            ? dayjs(b.timestamp).valueOf()
            : Number.POSITIVE_INFINITY;
          return aTimestamp - bTimestamp;
        } else if (sortBy === "opp-name") {
          const aName = a.name.toLowerCase();
          const bName = b.name.toLowerCase();

          const aIsLetter = /^[a-zA-Z]/.test(aName);
          const bIsLetter = /^[a-zA-Z]/.test(bName);
          if (aIsLetter && !bIsLetter) return 1;
          if (!aIsLetter && bIsLetter) return -1;
          return bName.localeCompare(aName);
        }
      }) || [];

  const updateAutomation = (payload) => {
    setAutomations((prevAutomations) => {
      const newAutomations = prevAutomations.map((automation) => {
        if (automation.id === payload.automationId) {
          return {
            ...automation,
            timestamp: payload.timestamp,
            running: payload.running || false,
          };
        }
        return automation;
      });
      return newAutomations;
    });

    // Also update task flow automations
    setTaskFlowAutomations((prevAutomations) => {
      const newAutomations = prevAutomations.map((automation) => {
        if (automation.id === payload.automationId) {
          return {
            ...automation,
            timestamp: payload.timestamp,
            running: payload.running || false,
          };
        }
        return automation;
      });
      return newAutomations;
    });

    // Also update task histories
    setTaskHistories((prevAutomations) => {
      const newAutomations = prevAutomations.map((automation) => {
        if (automation.id === payload.automationId) {
          return {
            ...automation,
            timestamp: payload.timestamp,
            running: payload.running || false,
          };
        }
        return automation;
      });
      return newAutomations;
    });
  };

  const handleListAutomations = async () => {
    try {
      const resp = await listAutomations(sendAndWaitWs, "standard");
      if (resp && Array.isArray(resp.automations)) {
        const allAutomations = resp["automations"];

        // Filter by type
        const recurringAutomations = allAutomations.filter(
          (automation) => automation.type === "RECURRING"
        );
        const taskFlowAutomations = allAutomations.filter(
          (automation) => automation.type === "TASK_FLOW"
        );

        // Collect all tags from both types
        const allTags = allAutomations.reduce(
          (acc, automation) => {
            if (Array.isArray(automation.tags) && automation.tags.length > 0) {
              automation.tags.forEach((tag) => {
                if (!acc.includes(tag)) {
                  acc.push(tag);
                }
              });
            }
            return acc;
          },
          ["all"]
        );

        setTags(allTags);
        setAutomations(recurringAutomations);
        setTaskFlowAutomations(taskFlowAutomations);
      } else {
        setAutomations([]);
        setTaskFlowAutomations([]);
      }
    } catch (error) {
      console.error("Failed to fetch automations:", error);
      setAutomations([]);
      setTaskFlowAutomations([]);
    }
  };

  const handleListTaskHistories = async () => {
    try {
      const resp = await listAutomations(sendAndWaitWs, "temp");
      if (resp && Array.isArray(resp.automations)) {
        const taskFlowAutomations = resp["automations"];
        setTaskHistories(taskFlowAutomations);
      } else {
        setTaskHistories([]);
      }
    } catch (error) {
      console.error("Failed to fetch task histories:", error);
      setTaskHistories([]);
    }
  };

  const handleListAll = async () => {
    setLoading(true);
    try {
      // Call both functions in parallel for better performance
      await Promise.all([handleListAutomations(), handleListTaskHistories()]);
    } catch (error) {
      console.error("Failed to fetch data:", error);
    } finally {
      setLoading(false);
    }
  };

  const openOverview = (data) => {
    history.push(`/automation/overview?id=${data.id}`);
  };

  const handleEnableDisable = async (event, enableDisable, automationId) => {
    setEnablingDisablingId(automationId);
    if (enableDisable) {
      const response = await enableAutomation(sendAndWaitWs, automationId);
      if (response && !response.error) {
        successToast("Enabled");
        const na = [...automations];
        na.filter((a) => a.id === automationId).forEach((a) => {
          a.enabled = response.enabled;
          if (response.corrupted) {
            a.corrupted = response.corrupted;
          }
        });
        setAutomations(na);
      } else {
        errorToast("Error!");
      }
    } else {
      const response = await disableAutomation(sendAndWaitWs, automationId);
      if (response && !response.error) {
        successToast("Disabled");
        const na = [...automations];
        na.filter((a) => a.id === automationId).forEach((a) => {
          a.enabled = response.enabled;
          if (response.corrupted) {
            a.corrupted = response.corrupted;
          }
        });
        setAutomations(na);
      } else {
        errorToast("Error!");
      }
    }
    setEnablingDisablingId(null);
  };

  const handleStartTask = async (event, runningStatus, automationId) => {
    if (runningStatus) {
      const success = await runningAutomation(sendAndWaitWs, automationId);
      if (success) {
        // pass
      } else {
        errorToast("Error starting the task");
      }
    }
  };

  const handleTagsChange = (tags) => {
    setSelectedTags(tags);
  };

  const handleEnabledChange = (able) => {
    setSelectedEnabled(able);
  };

  const handleErrorChange = (error) => {
    setSelectedError(error);
  };

  const handleRunningChange = (running) => {
    setSelectedRunning(running);
  };

  const handleCheckboxChange = (e, automationId) => {
    if (e.target.checked) {
      setSelectedRecurrings((prev) => [...prev, automationId]);
    } else {
      setSelectedRecurrings((prev) => prev.filter((id) => id !== automationId));
    }
  };

  const handleTaskFlowCheckboxChange = (e, automationId) => {
    if (e.target.checked) {
      setSelectedTaskFlows((prev) => [...prev, automationId]);
    } else {
      setSelectedTaskFlows((prev) => prev.filter((id) => id !== automationId));
    }
  };

  const handleTaskHistoryCheckboxChange = (e, automationId) => {
    if (e.target.checked) {
      setSelectedTaskHistories((prev) => [...prev, automationId]);
    } else {
      setSelectedTaskHistories((prev) =>
        prev.filter((id) => id !== automationId)
      );
    }
  };

  const handleConfirmDelete = () => {
    setDeleteConfirmOpen(true);
  };

  const handleDeleteSelected = async () => {
    setIsRunning("DELETE");
    try {
      // Collect all selected items from all tabs
      const allSelectedItems = [
        ...selectedRecurrings,
        ...selectedTaskFlows,
        ...selectedTaskHistories,
      ];

      const resp = await batchDelete(sendAndWaitWs, allSelectedItems, false);
      if (resp) {
        successToast("Success!");
        await handleListAll();
      }
    } catch (error) {
      errorToast("Error!");
    } finally {
      setIsMultiSelectMode(false);
      setSelectedRecurrings([]);
      setSelectedTaskFlows([]);
      setSelectedTaskHistories([]);
      setIsRunning("");
      setDeleteConfirmOpen(false);
    }
  };

  const handleEnterMultiSelectRecurring = (automationId) => {
    setIsMultiSelectMode(true);
    setSelectedRecurrings([automationId]);
  };

  const handleEnterMultiSelectTaskFlow = (automationId) => {
    setIsMultiSelectMode(true);
    setSelectedTaskFlows([automationId]);
  };

  const handleEnterMultiSelectTaskHistory = (automationId) => {
    setIsMultiSelectMode(true);
    setSelectedTaskHistories([automationId]);
  };

  const handleCancelMultiSelect = () => {
    setIsMultiSelectMode(false);
    setSelectedRecurrings([]);
    setSelectedTaskFlows([]);
    setSelectedTaskHistories([]);
  };

  const handleShowDeleteDialog = (automationId) => {
    setDeleteTargetId(automationId);
    setSingleDeleteConfirmOpen(true);
  };

  const handleSingleDelete = async () => {
    setIsRunning("DELETE");

    try {
      if (deleteTargetId) {
        const resp = await deleteAutomation(
          sendAndWaitWs,
          deleteTargetId,
          true
        );

        if (resp) {
          successToast("Successfully deleted!");
          await handleListAll();
        }
      } else errorToast("Failed to delete!");
    } catch (error) {
      errorToast("Failed to delete!");
    } finally {
      setIsRunning("");
      setSingleDeleteConfirmOpen(false);
      setDeleteTargetId(null);
    }
  };

  // Get the background color function with current global state
  const getComponentBgColor = useComponentBackgroundColor();

  // Helper functions for multi-select
  const getCurrentTabData = () => {
    switch (activeTab) {
      case 0:
        return sortedAutomations.filter((automation) => {
          const tagMatch =
            selectedTags.includes("all") ||
            (automation.tags &&
              automation.tags.filter((x) => selectedTags.includes(x)).length ===
                selectedTags.length);

          const ableMatch =
            selectedEnabled.includes("all") ||
            (selectedEnabled.includes("enabled") && automation.enabled) ||
            (selectedEnabled.includes("disabled") && !automation.enabled);

          const errorMatch =
            selectedError.includes("all") ||
            (selectedError.includes("error") && !automation.runnable) ||
            (selectedError.includes("no-error") && automation.runnable);

          const runningMatch =
            selectedRunning.includes("all") ||
            (selectedRunning.includes("running") && automation.running) ||
            (selectedRunning.includes("not-running") && !automation.running);

          return tagMatch && ableMatch && errorMatch && runningMatch;
        });
      case 1:
        return sortedTaskFlowAutomations.filter((automation) => {
          const tagMatch =
            selectedTags.includes("all") ||
            (automation.tags &&
              automation.tags.filter((x) => selectedTags.includes(x)).length ===
                selectedTags.length);

          const ableMatch = true;

          const errorMatch =
            selectedError.includes("all") ||
            (selectedError.includes("error") && !automation.runnable) ||
            (selectedError.includes("no-error") && automation.runnable);

          const runningMatch =
            selectedRunning.includes("all") ||
            (selectedRunning.includes("running") && automation.running) ||
            (selectedRunning.includes("not-running") && !automation.running);

          return tagMatch && ableMatch && errorMatch && runningMatch;
        });
      case 2:
        return sortedTaskHistories;
      default:
        return [];
    }
  };

  const getCurrentSelectedCount = () => {
    switch (activeTab) {
      case 0:
        return selectedRecurrings.length;
      case 1:
        return selectedTaskFlows.length;
      case 2:
        return selectedTaskHistories.length;
      default:
        return 0;
    }
  };

  const getTotalSelectedCount = () => {
    return (
      selectedRecurrings.length +
      selectedTaskFlows.length +
      selectedTaskHistories.length
    );
  };

  const handleCurrentTabSelectAll = () => {
    const currentTabData = getCurrentTabData();
    const selectedCount = getCurrentSelectedCount();

    switch (activeTab) {
      case 0:
        if (selectedCount === 0) {
          // Select all
          setSelectedRecurrings(
            currentTabData.map((automation) => automation.id)
          );
        } else if (selectedCount === currentTabData.length) {
          // Unselect all
          setSelectedRecurrings([]);
        } else {
          // Partial selection - select all
          setSelectedRecurrings(
            currentTabData.map((automation) => automation.id)
          );
        }
        break;
      case 1:
        if (selectedCount === 0) {
          // Select all
          setSelectedTaskFlows(
            currentTabData.map((automation) => automation.id)
          );
        } else if (selectedCount === currentTabData.length) {
          // Unselect all
          setSelectedTaskFlows([]);
        } else {
          // Partial selection - select all
          setSelectedTaskFlows(
            currentTabData.map((automation) => automation.id)
          );
        }
        break;
      case 2:
        if (selectedCount === 0) {
          // Select all
          setSelectedTaskHistories(
            currentTabData.map((automation) => automation.id)
          );
        } else if (selectedCount === currentTabData.length) {
          // Unselect all
          setSelectedTaskHistories([]);
        } else {
          // Partial selection - select all
          setSelectedTaskHistories(
            currentTabData.map((automation) => automation.id)
          );
        }
        break;
    }
  };

  const handleCardLongPress = (automationId) => {
    setShakingCardId(automationId);
    setTimeout(() => setShakingCardId(null), 500);
  };

  return (
    <>
      <GlobalStyle />
      {loading ? (
        <></>
      ) : (
        <div className="flex-grow mt-1 mx-2 pb-12">
          {isShowFilter && (
            <AutomationFilter
              sortBy={sortBy}
              setSortBy={setSortBy}
              setFilterText={setFilterText}
              setShowFilter={setShowFilter}
              onTagsChange={handleTagsChange}
              onEnabledChange={handleEnabledChange}
              onErrorChange={handleErrorChange}
              onRunningChange={handleRunningChange}
              tags={tags}
              type="recurring"
            />
          )}
          {isMultiSelectMode && (
            <div
              className="fixed left-0 bottom-0 px-2 py-1.5 z-[2000] flex justify-between items-center"
              style={{
                width:
                  isMdOrLgScreen && isChatSidebarVisible
                    ? "calc(100vw - 400px)"
                    : "100vw",
                backgroundColor: "hsl(var(--card))",
                boxShadow:
                  "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
              }}
            >
              {/* Left side - Count */}
              <div className="flex items-center">
                <Typography variant="body1" className="font-medium">
                  {`${getTotalSelectedCount()} selected`}
                </Typography>
              </div>

              {/* Right side - Checkbox and Action buttons */}
              <div className="flex items-center gap-1">
                <Checkbox
                  indeterminate={(() => {
                    const currentTabData = getCurrentTabData();
                    const selectedCount = getCurrentSelectedCount();
                    return (
                      selectedCount > 0 && selectedCount < currentTabData.length
                    );
                  })()}
                  checked={(() => {
                    const currentTabData = getCurrentTabData();
                    const selectedCount = getCurrentSelectedCount();
                    return (
                      selectedCount === currentTabData.length &&
                      currentTabData.length > 0
                    );
                  })()}
                  onChange={handleCurrentTabSelectAll}
                  color="primary"
                />

                <Tooltip title="Delete selected items">
                  <span style={{ pointerEvents: "auto" }}>
                    <IconButton
                      color="error"
                      size="medium"
                      disabled={getTotalSelectedCount() === 0}
                      onClick={handleConfirmDelete}
                    >
                      <DeleteIcon fontSize="medium" />
                    </IconButton>
                  </span>
                </Tooltip>

                <Tooltip title="Cancel Multi-Select">
                  <span style={{ pointerEvents: "auto" }}>
                    <IconButton
                      color="default"
                      size="medium"
                      onClick={handleCancelMultiSelect}
                    >
                      <CloseIcon fontSize="medium" />
                    </IconButton>
                  </span>
                </Tooltip>
              </div>
            </div>
          )}

          {/* Tab Navigation */}
          <div>
            <Tabs
              value={activeTab}
              onChange={(event, newValue) => setActiveTab(newValue)}
              variant="standard"
              style={{ width: "100%" }}
            >
              <Tab
                label={`Automatic Run (${
                  sortedAutomations.filter((automation) => {
                    const tagMatch =
                      selectedTags.includes("all") ||
                      (automation.tags &&
                        automation.tags.filter((x) => selectedTags.includes(x))
                          .length === selectedTags.length);

                    const ableMatch =
                      selectedEnabled.includes("all") ||
                      (selectedEnabled.includes("enabled") &&
                        automation.enabled) ||
                      (selectedEnabled.includes("disabled") &&
                        !automation.enabled);

                    const errorMatch =
                      selectedError.includes("all") ||
                      (selectedError.includes("error") &&
                        !automation.runnable) ||
                      (selectedError.includes("no-error") &&
                        automation.runnable);

                    const runningMatch =
                      selectedRunning.includes("all") ||
                      (selectedRunning.includes("running") &&
                        automation.running) ||
                      (selectedRunning.includes("not-running") &&
                        !automation.running);

                    return tagMatch && ableMatch && errorMatch && runningMatch;
                  }).length
                })`}
                style={{
                  textTransform: "none",
                  fontSize: "1rem",
                  fontWeight: 500,
                  flex: 1,
                  minWidth: 0,
                  color:
                    activeTab === 0
                      ? "hsl(var(--primary))"
                      : "hsl(var(--muted-foreground))",
                }}
              />
              <Tab
                label={`Manually Run (${
                  sortedTaskFlowAutomations.filter((automation) => {
                    const tagMatch =
                      selectedTags.includes("all") ||
                      (automation.tags &&
                        automation.tags.filter((x) => selectedTags.includes(x))
                          .length === selectedTags.length);

                    const ableMatch = true;

                    const errorMatch =
                      selectedError.includes("all") ||
                      (selectedError.includes("error") &&
                        !automation.runnable) ||
                      (selectedError.includes("no-error") &&
                        automation.runnable);

                    const runningMatch =
                      selectedRunning.includes("all") ||
                      (selectedRunning.includes("running") &&
                        automation.running) ||
                      (selectedRunning.includes("not-running") &&
                        !automation.running);

                    return tagMatch && ableMatch && errorMatch && runningMatch;
                  }).length
                })`}
                style={{
                  textTransform: "none",
                  fontSize: "1rem",
                  fontWeight: 500,
                  flex: 1,
                  minWidth: 0,
                  color:
                    activeTab === 1
                      ? "hsl(var(--primary))"
                      : "hsl(var(--muted-foreground))",
                }}
              />
              <Tab
                label={`History (${sortedTaskHistories.length})`}
                style={{
                  textTransform: "none",
                  fontSize: "1rem",
                  fontWeight: 500,
                  flex: 1,
                  minWidth: 0,
                  color:
                    activeTab === 2
                      ? "hsl(var(--primary))"
                      : "hsl(var(--muted-foreground))",
                }}
              />
            </Tabs>
          </div>

          {/* Tab Content */}
          {activeTab === 0 &&
            (automations.length !== 0 ? (
              <ResponsiveCardGrid
                spacing={{ xs: 1.5, sm: 2 }}
                breakpoints={{ xs: 1, sm: 2, md: 3, lg: 4 }}
              >
                {sortedAutomations
                  .filter((automation) => {
                    const tagMatch =
                      selectedTags.includes("all") ||
                      (automation.tags &&
                        automation.tags.filter((x) => selectedTags.includes(x))
                          .length === selectedTags.length);

                    const ableMatch =
                      selectedEnabled.includes("all") ||
                      (selectedEnabled.includes("enabled") &&
                        automation.enabled) ||
                      (selectedEnabled.includes("disabled") &&
                        !automation.enabled);

                    const errorMatch =
                      selectedError.includes("all") ||
                      (selectedError.includes("error") &&
                        !automation.runnable) ||
                      (selectedError.includes("no-error") &&
                        automation.runnable);

                    const runningMatch =
                      selectedRunning.includes("all") ||
                      (selectedRunning.includes("running") &&
                        automation.running) ||
                      (selectedRunning.includes("not-running") &&
                        !automation.running);

                    return tagMatch && ableMatch && errorMatch && runningMatch;
                  })
                  .map((automation, index) => {
                    const menuItems = [
                      {
                        label: "Multi Select",
                        icon: <CheckBoxOutlineBlankIcon fontSize="small" />,
                        onClick: () =>
                          handleEnterMultiSelectRecurring(automation.id),
                      },
                      {
                        label: "Delete",
                        icon: <DeleteIcon fontSize="small" />,
                        onClick: () => handleShowDeleteDialog(automation.id),
                        color: "hsl(var(--destructive))",
                      },
                    ];

                    return (
                      <MenuCard
                        key={index}
                        menuItems={menuItems}
                        onCardClick={() => {
                          if (!isMultiSelectMode) {
                            openOverview(automation);
                          }
                        }}
                        onLongPress={() => handleCardLongPress(automation.id)}
                        elevation={0}
                        style={{
                          backgroundColor:
                            getComponentBgColor("hsl(var(--card))"),
                          animation:
                            shakingCardId === automation.id
                              ? "shake 0.5s ease-in-out"
                              : "none",
                        }}
                      >
                        <CardContent
                          className="h-full flex flex-col"
                          style={{ padding: "8px" }}
                        >
                          <div className="flex justify-between items-center mb-2 min-h-[24px]">
                            {/* Tag Icons */}
                            <div className="flex gap-2 items-center overflow-hidden flex-1 mr-4">
                              {automation.tags &&
                                automation.tags.length > 0 &&
                                automation.tags.map((tag, tagIndex) => (
                                  <div
                                    key={tagIndex}
                                    className="flex items-center"
                                  >
                                    {getTagIcon(tag, 16)}
                                  </div>
                                ))}
                            </div>

                            {/* Control buttons */}
                            {isMultiSelectMode ? (
                              <Checkbox
                                checked={selectedRecurrings.includes(
                                  automation.id
                                )}
                                onChange={(e) => {
                                  e.stopPropagation();
                                  handleCheckboxChange(e, automation.id);
                                }}
                                className="absolute top-0 right-0 z-[1]"
                              />
                            ) : (
                              <>
                                {automation.blocked ? (
                                  <Tooltip title="Insufficient tokens - subscription ended or used up">
                                    <TokenIcon
                                      style={{
                                        color: "hsl(var(--destructive))",
                                        marginRight: "15px",
                                        marginLeft: "15px",
                                      }}
                                    />
                                  </Tooltip>
                                ) : !automation.runnable ? (
                                  <ErrorIcon
                                    style={{
                                      color: "red",
                                      marginRight: "15px",
                                      marginLeft: "15px",
                                    }}
                                  />
                                ) : enablingDisablingId === automation.id ? (
                                  <CircularProgress size={24} />
                                ) : (
                                  <MSwitch
                                    checked={automation.enabled}
                                    disabled={!automation.runnable}
                                    onClick={(e) => e.stopPropagation()}
                                    onChange={(e, b) =>
                                      handleEnableDisable(e, b, automation.id)
                                    }
                                    inputProps={{ "aria-label": "controlled" }}
                                  />
                                )}
                              </>
                            )}
                          </div>

                          {/* Second row: Automation Name */}
                          <Typography
                            gutterBottom
                            variant="body1"
                            component="div"
                            className="line-clamp-3 overflow-hidden text-ellipsis max-h-[4.5em] mb-0"
                          >
                            {automation.name}
                          </Typography>

                          {automation.timestamp !== 0 ? (
                            <div>
                              <Typography
                                variant="caption"
                                component="div"
                                className="line-clamp-3 overflow-hidden text-ellipsis mt-1"
                                style={{
                                  color: "hsl(var(--muted-foreground))",
                                }}
                              >
                                {(sortBy === "created" ||
                                  sortBy === "opp-created") &&
                                automation.createdAt
                                  ? `Created: ${customFormat(
                                      dayjs(automation.createdAt),
                                      currentTime
                                    )}`
                                  : `Last run: ${customFormat(
                                      dayjs(automation.timestamp),
                                      currentTime
                                    )}`}
                              </Typography>
                            </div>
                          ) : (
                            <div>
                              <Typography
                                variant="caption"
                                component="div"
                                className="line-clamp-3 overflow-hidden text-ellipsis mt-1"
                                style={{
                                  color: "hsl(var(--muted-foreground))",
                                }}
                              >
                                {(sortBy === "created" ||
                                  sortBy === "opp-created") &&
                                automation.createdAt
                                  ? `Created: ${customFormat(
                                      dayjs(automation.createdAt),
                                      currentTime
                                    )}`
                                  : "Not run yet"}
                              </Typography>
                            </div>
                          )}
                        </CardContent>
                      </MenuCard>
                    );
                  })}
              </ResponsiveCardGrid>
            ) : (
              <div className="flex flex-col justify-center items-center text-center w-full">
                <Typography
                  variant="h6"
                  color="text.secondary"
                  className="mb-1"
                >
                  No recurring automations
                </Typography>
                <Typography
                  variant="body1"
                  color="text.secondary"
                  className="whitespace-pre-line w-full"
                >
                  Ask assistant to create new one
                </Typography>
                <Typography
                  variant="body1"
                  color="text.secondary"
                  className="whitespace-pre-line w-full"
                >
                  Or ask the chatbot
                </Typography>
              </div>
            ))}

          {/* Task Flow Tab */}
          {activeTab === 1 &&
            (taskFlowAutomations.length !== 0 ? (
              <ResponsiveCardGrid
                spacing={{ xs: 1, sm: 2 }}
                breakpoints={{ xs: 1, sm: 2, md: 3, lg: 4 }}
              >
                {sortedTaskFlowAutomations
                  .filter((automation) => {
                    const tagMatch =
                      selectedTags.includes("all") ||
                      (automation.tags &&
                        automation.tags.filter((x) => selectedTags.includes(x))
                          .length === selectedTags.length);

                    const ableMatch = true;

                    const errorMatch =
                      selectedError.includes("all") ||
                      (selectedError.includes("error") &&
                        !automation.runnable) ||
                      (selectedError.includes("no-error") &&
                        automation.runnable);

                    const runningMatch =
                      selectedRunning.includes("all") ||
                      (selectedRunning.includes("running") &&
                        automation.running) ||
                      (selectedRunning.includes("not-running") &&
                        !automation.running);

                    return tagMatch && ableMatch && errorMatch && runningMatch;
                  })
                  .map((automation, index) => {
                    const menuItems = [
                      {
                        label: "Multi Select",
                        icon: <CheckBoxOutlineBlankIcon fontSize="small" />,
                        onClick: () =>
                          handleEnterMultiSelectTaskFlow(automation.id),
                      },
                      {
                        label: "Delete",
                        icon: <DeleteIcon fontSize="small" />,
                        onClick: () => handleShowDeleteDialog(automation.id),
                        color: "hsl(var(--destructive))",
                      },
                    ];

                    return (
                      <MenuCard
                        key={index}
                        menuItems={menuItems}
                        onCardClick={() => {
                          if (!isMultiSelectMode) {
                            openOverview(automation);
                          }
                        }}
                        onLongPress={() => handleCardLongPress(automation.id)}
                        elevation={0}
                        style={{
                          backgroundColor:
                            getComponentBgColor("hsl(var(--card))"),
                          animation:
                            shakingCardId === automation.id
                              ? "shake 0.5s ease-in-out"
                              : "none",
                        }}
                      >
                        <CardContent
                          className="h-full flex flex-col"
                          style={{ padding: "8px" }}
                        >
                          <div className="flex justify-between items-center mb-2 min-h-[24px]">
                            {/* Tag Icons */}
                            <div className="flex gap-2 items-center overflow-hidden flex-1 mr-4">
                              {automation.tags &&
                                automation.tags.length > 0 &&
                                automation.tags.map((tag, tagIndex) => (
                                  <div
                                    key={tagIndex}
                                    className="flex items-center"
                                  >
                                    {getTagIcon(tag, 16)}
                                  </div>
                                ))}
                            </div>

                            {/* Control buttons */}
                            {isMultiSelectMode ? (
                              <Checkbox
                                checked={selectedTaskFlows.includes(
                                  automation.id
                                )}
                                onChange={(e) => {
                                  e.stopPropagation();
                                  handleTaskFlowCheckboxChange(
                                    e,
                                    automation.id
                                  );
                                }}
                                className="absolute top-0 right-0 z-[1]"
                              />
                            ) : (
                              <>
                                {automation.blocked ? (
                                  <Tooltip title="Insufficient tokens - subscription ended or used up">
                                    <TokenIcon
                                      style={{
                                        color: "hsl(var(--destructive))",
                                      }}
                                    />
                                  </Tooltip>
                                ) : automation.runnable &&
                                  automation.running ? (
                                  <CircularProgress size={24} />
                                ) : automation.runnable ? (
                                  <PlayCircleFilledWhiteIcon
                                    disabled={!automation.runnable}
                                    onClick={(e) => {
                                      handleStartTask(e, true, automation.id);
                                      e.stopPropagation();
                                    }}
                                    style={{
                                      cursor: "pointer",
                                      color: "hsl(var(--primary))",
                                    }}
                                  />
                                ) : (
                                  <ErrorIcon
                                    style={{
                                      color: "red",
                                    }}
                                  />
                                )}
                              </>
                            )}
                          </div>

                          {/* Second row: Automation Name */}
                          <Typography
                            gutterBottom
                            variant="body1"
                            component="div"
                            className="line-clamp-3 overflow-hidden text-ellipsis max-h-[4.5em] mb-0"
                          >
                            {automation.name}
                          </Typography>

                          {automation.timestamp !== 0 ? (
                            <div>
                              <Typography
                                variant="caption"
                                component="div"
                                className="line-clamp-3 overflow-hidden text-ellipsis mt-1"
                                style={{
                                  color: "hsl(var(--muted-foreground))",
                                }}
                              >
                                {(sortBy === "created" ||
                                  sortBy === "opp-created") &&
                                automation.createdAt
                                  ? `Created: ${customFormat(
                                      dayjs(automation.createdAt),
                                      currentTime
                                    )}`
                                  : `Last run: ${customFormat(
                                      dayjs(automation.timestamp),
                                      currentTime
                                    )}`}
                              </Typography>
                            </div>
                          ) : (
                            <div>
                              <Typography
                                variant="caption"
                                component="div"
                                className="line-clamp-3 overflow-hidden text-ellipsis mt-1"
                                style={{
                                  color: "hsl(var(--muted-foreground))",
                                }}
                              >
                                {(sortBy === "created" ||
                                  sortBy === "opp-created") &&
                                automation.createdAt
                                  ? `Created: ${customFormat(
                                      dayjs(automation.createdAt),
                                      currentTime
                                    )}`
                                  : "Never executed"}
                              </Typography>
                            </div>
                          )}
                        </CardContent>
                      </MenuCard>
                    );
                  })}
              </ResponsiveCardGrid>
            ) : (
              <div className="flex flex-col justify-center items-center text-center w-full">
                <Typography
                  variant="h6"
                  color="text.secondary"
                  className="mb-1"
                >
                  No task flow automations
                </Typography>
                <Typography
                  variant="body1"
                  color="text.secondary"
                  className="whitespace-pre-line w-full"
                >
                  Ask assistant to create new one
                </Typography>
                <Typography
                  variant="body1"
                  color="text.secondary"
                  className="whitespace-pre-line w-full"
                >
                  Or ask the assistant
                </Typography>
              </div>
            ))}

          {/* Task History Tab */}
          {activeTab === 2 && (
            <ResponsiveCardGrid
              spacing={{ xs: 1, sm: 2 }}
              breakpoints={{ xs: 1, sm: 2, md: 3, lg: 4 }}
            >
              {sortedTaskHistories.map((automation, index) => {
                const menuItems = [
                  {
                    label: "Multi Select",
                    icon: <CheckBoxOutlineBlankIcon fontSize="small" />,
                    onClick: () =>
                      handleEnterMultiSelectTaskHistory(automation.id),
                  },
                  {
                    label: "Delete",
                    icon: <DeleteIcon fontSize="small" />,
                    onClick: () => handleShowDeleteDialog(automation.id),
                    color: "hsl(var(--destructive))",
                  },
                ];

                return (
                  <MenuCard
                    key={index}
                    menuItems={menuItems}
                    onCardClick={() => {
                      if (!isMultiSelectMode) {
                        openOverview(automation);
                      }
                    }}
                    onLongPress={() => handleCardLongPress(automation.id)}
                    elevation={0}
                    style={{
                      backgroundColor: getComponentBgColor("hsl(var(--card))"),
                      animation:
                        shakingCardId === automation.id
                          ? "shake 0.5s ease-in-out"
                          : "none",
                    }}
                  >
                    <CardContent className="p-2 h-full flex flex-col">
                      <div className="flex justify-between items-center mb-2 min-h-[24px]">
                        {/* Tag Icons */}
                        <div className="flex gap-2 items-center overflow-hidden flex-1 mr-4">
                          {automation.tags &&
                            automation.tags.length > 0 &&
                            automation.tags.map((tag, tagIndex) => (
                              <div key={tagIndex} className="flex items-center">
                                {getTagIcon(tag, 16)}
                              </div>
                            ))}
                        </div>

                        {/* Control buttons */}
                        {isMultiSelectMode ? (
                          <Checkbox
                            checked={selectedTaskHistories.includes(
                              automation.id
                            )}
                            onChange={(e) => {
                              e.stopPropagation();
                              handleTaskHistoryCheckboxChange(e, automation.id);
                            }}
                            className="absolute top-0 right-0 z-[1]"
                          />
                        ) : (
                          <>
                            {automation.blocked ? (
                              <Tooltip title="Insufficient tokens - subscription ended or used up">
                                <TokenIcon
                                  style={{
                                    color: "hsl(var(--destructive))",
                                  }}
                                />
                              </Tooltip>
                            ) : automation.runnable && automation.running ? (
                              <CircularProgress size={24} />
                            ) : automation.runnable ? (
                              <PlayCircleFilledWhiteIcon
                                onClick={(e) => {
                                  handleStartTask(e, true, automation.id);
                                  e.stopPropagation();
                                }}
                                style={{
                                  cursor: "pointer",
                                  color: "hsl(var(--primary))",
                                }}
                              />
                            ) : (
                              <ErrorIcon
                                style={{
                                  color: "red",
                                }}
                              />
                            )}
                          </>
                        )}
                      </div>

                      {/* Second row: Automation Name */}
                      <Typography
                        gutterBottom
                        variant="body1"
                        component="div"
                        className="line-clamp-3 overflow-hidden text-ellipsis max-h-[4.5em] mb-0"
                      >
                        {automation.name}
                      </Typography>

                      <div>
                        <Typography
                          variant="caption"
                          component="div"
                          className="line-clamp-3 overflow-hidden text-ellipsis mt-1"
                          style={{ color: "hsl(var(--muted-foreground))" }}
                        >
                          {`Created: ${customFormat(
                            dayjs(automation.createdAt),
                            currentTime
                          )}`}
                        </Typography>
                      </div>
                    </CardContent>
                  </MenuCard>
                );
              })}
            </ResponsiveCardGrid>
          )}
        </div>
      )}
      <Dialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        variant="pops"
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete selected item(s)?
          </Typography>
        </DialogContent>
        <DialogActions className="flex justify-between px-2 pb-2">
          <Button
            onClick={() => setDeleteConfirmOpen(false)}
            variant="outlined"
            style={{ textTransform: "none", boxShadow: "none" }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteSelected}
            color="error"
            variant="contained"
            disabled={isRunning === "DELETE"}
            style={{ textTransform: "none", boxShadow: "none" }}
          >
            Delete
            {isRunning === "DELETE" && (
              <span className="ml-1 inline-flex items-center">
                <CircularProgress size={16} />
              </span>
            )}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={singleDeleteConfirmOpen}
        onClose={() => setSingleDeleteConfirmOpen(false)}
        variant="pops"
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete this item?</Typography>
        </DialogContent>
        <DialogActions className="flex justify-between px-2 pb-2">
          <Button
            variant="outlined"
            onClick={() => setSingleDeleteConfirmOpen(false)}
            style={{ textTransform: "none", boxShadow: "none" }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSingleDelete}
            color="error"
            variant="contained"
            disabled={isRunning === "DELETE"}
            style={{ textTransform: "none", boxShadow: "none" }}
          >
            Delete
            {isRunning === "DELETE" && (
              <span className="ml-1 inline-flex items-center">
                <CircularProgress size={16} />
              </span>
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default AutomationListView;
